import React, { useState, useEffect } from 'react';
import { GraduationCap } from 'lucide-react';
import { getExecutiveEduScore } from '../../services/api';
import './ExecutiveEduScore.css';

const getScoreColor = (score) => {
  if (score >= 40) return '#10b981';
  if (score >= 25) return '#3b82f6';
  if (score >= 15) return '#f59e0b';
  return '#9ca3af';
};

const getScoreTier = (score) => {
  if (score >= 40) return 'high';
  if (score >= 25) return 'mid';
  if (score >= 15) return 'low';
  return 'vlow';
};

const getTierBadgeClass = (tier) => {
  switch (tier) {
    case 'TOP':        return 'edu-tier-badge edu-tier-top';
    case 'STRONG':     return 'edu-tier-badge edu-tier-strong';
    case 'RECOGNIZED': return 'edu-tier-badge edu-tier-recognized';
    default:           return 'edu-tier-badge edu-tier-other';
  }
};

const TIER_LABEL = { TOP: 'TOP', STRONG: 'STRONG', RECOGNIZED: 'RECOG' };

const SkeletonLoader = () => (
  <div className="feature-section">
    <div className="flex items-center gap-2 mb-2">
      <div className="feature-icon"><GraduationCap size={20} /></div>
      <div className="edu-skeleton edu-skeleton-title" />
    </div>
    <div className="edu-skeleton edu-skeleton-body" />
  </div>
);

const ExecutiveEduScore = ({ ticker }) => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);

  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    setLoading(true);
    setError(false);
    setData(null);

    getExecutiveEduScore(ticker)
      .then((result) => { if (!cancelled) { setData(result); setLoading(false); } })
      .catch(() => { if (!cancelled) { setError(true); setLoading(false); } });

    return () => { cancelled = true; };
  }, [ticker]);

  if (loading) return <SkeletonLoader />;
  if (error || data === null) return null;
  if (!data.score || !data.executiveCount) return null;

  const { score, executiveCount, topSchool, tier, executives } = data;
  const scoreColor = getScoreColor(score);
  const scoreTier  = getScoreTier(score);
  const displayExecs = executives ? executives.slice(0, 5) : [];

  return (
    <div className="feature-section">
      {/* 섹션 헤더 */}
      <div className="flex items-center justify-between gap-1 mb-1">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <div className="feature-icon">
              <GraduationCap size={20} />
            </div>
            <h2 className="text-xm font-bold text-black">학력 점수</h2>
            <span className="edu-fun-badge">스낵 콘텐츠</span>
          </div>
          <p className="text-xs text-gray-600 pl-1">
            SEC 공시 자료를 AI가 분석하여 경영진의 학력 배경을 점수화합니다.
          </p>
        </div>
      </div>

      {/* 내부 카드 */}
      <div className={`edu-inner-card edu-inner-card--${scoreTier}`}>
        {/* 스코어 행 */}
        <div className="edu-score-row">
          <div className="edu-score-badge">
            <span className="edu-score-num" style={{ color: scoreColor }}>{score}</span>
            <span className="edu-score-unit" style={{ color: scoreColor }}>pts</span>
          </div>
          <div className="edu-score-right">
            <span className="edu-tier-label">{tier}</span>
            <div className="edu-meta-chips">
              {executiveCount != null && (
                <span className="edu-meta-chip">임원 {executiveCount}명</span>
              )}
              {topSchool && (
                <span className="edu-meta-chip edu-top-school-chip" title={topSchool}>
                  {topSchool}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* 임원 리스트 */}
        {displayExecs.length > 0 && (
          <div className="edu-executive-list">
            {displayExecs.map((exec, idx) => (
              <div key={idx} className="edu-executive-row">
                {exec.title && (
                  <span className="edu-exec-title" title={exec.title}>{exec.title}</span>
                )}
                <span className="edu-exec-name">{exec.name}</span>
                {exec.school && (
                  <div className="edu-exec-school-wrap">
                    <span className="edu-exec-school">{exec.school}</span>
                    {exec.tier && exec.tier !== '기타' && (
                      <span className={getTierBadgeClass(exec.tier)}>
                        {TIER_LABEL[exec.tier] ?? exec.tier}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* 면책 */}
        <div className="edu-disclaimer">
          재미로 보는 지표입니다 · AI 분석 알고리즘엔 포함되지 않습니다
        </div>
      </div>
    </div>
  );
};

export default ExecutiveEduScore;
